import { buildPrompt } from '@/ai/utils/promptBuilder'
import { examples } from '@/ai/prompts/common/examples'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { getJsonArrayOutputFormatting } from '@/ai/prompts/common/formatting'
import { createSystemMessage, createUserMessage } from '@/ai/utils/promptUtils'

export const buildWorkflowDiagramPrompt = ({ workflow }) => {
  const messages = []

  const systemMessageSections = []

  systemMessageSections.push({ content: systemMessages.EVENT_MODELING_DDD_EXPERT })

  if (workflow.aiVersion === 5) {
    // Used in case of OpenAI version 3.5
    systemMessageSections.push({
      title: 'Workflow Description',
      content: `\`${workflow.aiDescription}\`` || 'N/A'
    })

    systemMessageSections.push({
      title: 'Instructions',
      contentType: 'bullet',
      content: [
        'Generate limited number of state-changing events in chronological order based on the Workflow Description.',
        'Ensure that each event is unique and does not repeat.',
        'Arrange events logically from start to end, with no backtracking or looping.',
        'Always provide the response in valid JSON format without additional explanatory text.',
        'If any details are unclear or missing, make reasonable assumptions based on your expertise. Do not ask for clarifications.',
        'Follow the JSON example that will be provided in the user messages.'
      ]
    })
  }

  const systemContent = buildPrompt(systemMessageSections)
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  promptSections.push({
    title: 'Request',
    content: [
      `This request covers the first two steps of seven in Event Modeling: brainstorming events and
       arranging them on a timeline. Your task is to list all the state-changing events in chronological
       order based on the "Workflow Description" (a business process or project scope).
       A state-changing event is an actor invoking a state-changing command (create, update or delete) on an aggregate.
       Each step must include the following:`,
      `- **Role:** The actor (a human or a system if the step is automated) invoking the command,
       with a maximum length of 18 characters.`,
      `- **Event Description:** A description of the state-changing event using a verb in past tense and
       noun (the noun is the aggregate being created, updated, or deleted), with a maximum length of 40 characters.`
    ]
  })

  promptSections.push({
    title: 'Workflow Description',
    content: `\`${workflow.aiDescription}\`` || 'N/A'
  })

  promptSections.push({
    title: 'Note',
    contentType: 'bullet',
    content: [
      'Ensure role names and descriptions are concise and within the character limits.',
      'If the "Workflow Description" is unclear, make reasonable assumptions.',
      `State-changing events are actions or commands that alter the state of an aggregate.
       For example, "Created order" or "Approved invoice" are state-changing events, while actions
       like "Page viewed" are not, as they do not alter the state of an aggregate.`,
      `The name of the aggregate (e.g. order, issue, delivery) should align with how it would be
       named in a business process. For example, use "Picked up delivery" or "Picked up shipment"
       rather than "Picked up food" or "Picked up package".`
    ]
  })

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: getJsonArrayOutputFormatting(JSON.stringify(examples.workflowDiagram))
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
